<template>
  <div class="container-fluid py-4">
    <!-- Page Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">{{ $t('compare.productComparisonDashboard') }}</h2>
      
    </div>

    <div v-if="loading" class="text-center">
      <div class="spinner-border" role="status"><span class="visually-hidden">{{ $t('compare.loading') }}</span></div>
    </div>
    <div v-if="error" class="alert alert-danger">{{ $t('compare.failedToLoadProductsData') }}</div>

    <!-- Analysis Summary -->
    <div v-if="categoryAnalysisData.length > 0" class="row mb-4">
      <div class="col-12">
        <div class="card border-0 shadow-sm" style="background: linear-gradient(135deg, #28a745, #20c997);">
          <div class="card-body text-white">
            <div class="row align-items-center">
              <div class="col-md-8">
                <h5 class="mb-1">
                  <i class="fas fa-chart-line me-2"></i>{{ $t('compare.marketAnalysisDashboard') }}
                </h5>
                <p class="mb-0 opacity-75">
                  {{ $t('compare.realtimeAnalysisOf') }} {{ categoryAnalysisData.length }} {{ $t('compare.productCategories') }}
                </p>
              </div>
              <div class="col-md-4 text-md-end">
                <div class="d-flex justify-content-md-end gap-3">
                  <div class="text-center">
                    <div class="h4 mb-0">{{ totalProductsAnalyzed }}</div>
                    <small class="opacity-75">{{ $t('compare.productsAnalyzed') }}</small>
                  </div>
                  <div class="text-center">
                    <div class="h4 mb-0">{{ categoryAnalysisData.length }}</div>
                    <small class="opacity-75">{{ $t('compare.categories') }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Product Selection and Comparison -->
    <div class="row mb-4">
      <div class="col-lg-6 mb-3">
        <!-- Select Products to Compare -->
        <div class="card border-0 shadow-sm mb-3" style="background: linear-gradient(135deg, #6f42c1, #8b5cf6);">
          <div class="card-body text-white">
            <h5 class="mb-0">{{ $t('compare.selectProductsToCompare') }}</h5>
          </div>
        </div>
        
        <div class="card border-0 shadow-sm">
          <div class="card-body">
            <div class="mb-3">
              <label class="form-label">{{ $t('compare.categoryFilter') }}</label>
              <div class="d-flex gap-2">
                <select class="form-select" v-model="selectedCategory" @change="filterProducts">
                  <option value="">{{ $t('compare.allCategories') }}</option>
                  <option v-for="category in availableCategories" :key="category" :value="category">
                    {{ category }}
                  </option>
                </select>
              </div>
            </div>
            
            <div class="mb-3">
              <label class="form-label">{{ $t('compare.brandFilter') }}</label>
              <div class="d-flex gap-2">
                <select class="form-select" v-model="selectedBrand" @change="filterProducts">
                  <option value="">{{ $t('compare.allBrands') }}</option>
                  <option v-for="brand in availableBrands" :key="brand" :value="brand">
                    {{ brand }}
                  </option>
                </select>
              </div>
            </div>
            
            <div class="mb-3">
              <label class="form-label">{{ $t('compare.selectProductsUpTo5') }}</label>
              <div class="border rounded p-3 bg-light" style="max-height: 300px; overflow-y: auto;">
                <div v-for="product in filteredProducts" :key="product.id" class="mb-2">
                  <div class="d-flex align-items-center">
                    <input 
                      type="checkbox" 
                      class="form-check-input me-2 fixed-checkbox-width" 
                      :id="`product-${product.id}`"
                      v-model="selectedProducts"
                      :value="product.id"
                      :disabled="selectedProducts.length >= 5 && !selectedProducts.includes(product.id)"
                    >
                    <label :for="`product-${product.id}`" class="form-check-label flex-grow-1">
                      <div class="d-flex justify-content-between align-items-start">
                        <div>
                          <strong>{{ product.brand }}</strong> - {{ product.name }}
                          <br>
                          <small class="text-muted">{{ product.category }} | ${{ product.price }}</small>
                        </div>
                        <div class="text-end">
                          <span class="badge bg-primary fixed-rating-width">{{ product.rating }}/5</span>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="d-flex gap-2">
              <button class="btn btn-primary" @click="compareProducts" :disabled="selectedProducts.length < 2">
                <i class="fas fa-balance-scale me-1"></i>{{ $t('compare.compareSelectedProducts') }} ({{ selectedProducts.length }})
              </button>
              <button class="btn btn-outline-secondary" @click="clearSelection">
                <i class="fas fa-times me-1"></i>{{ $t('compare.clearSelection') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div class="col-lg-6 mb-3">
        <!-- Selected Products -->
        <div class="card border-0 shadow-sm mb-3" style="background: linear-gradient(135deg, #6f42c1, #8b5cf6);">
          <div class="card-body text-white">
            <h5 class="mb-0">{{ $t('compare.selectedProducts') }} ({{ selectedProducts.length }}/5)</h5>
          </div>
        </div>
        
        <div class="card border-0 shadow-sm">
          <div class="card-body" style="min-height: 300px;">
            <div v-if="selectedProducts.length === 0" class="text-center py-5">
              <i class="fas fa-box-open fa-3x text-muted opacity-50 mb-3"></i>
              <p class="text-muted mb-0">{{ $t('compare.noProductsSelectedForComparison') }}</p>
            </div>
            <div v-else>
              <div v-for="productId in selectedProducts" :key="productId" class="mb-3 p-3 border rounded bg-light">
                <div class="d-flex justify-content-between align-items-start">
                  <div class="flex-grow-1">
                    <h6 class="mb-1">{{ getProductById(productId).brand }} {{ getProductById(productId).name }}</h6>
                    <div class="d-flex gap-3 mb-2">
                      <small class="text-muted">{{ getProductById(productId).category }}</small>
                      <small class="text-success fw-bold">${{ getProductById(productId).price }}</small>
                      <small class="text-warning">★ {{ getProductById(productId).rating }}/5</small>
                    </div>
                    <div class="d-flex gap-2">
                      <span class="badge bg-info">{{ getProductById(productId).specs.capacity || getProductById(productId).specs.speed }}</span>
                      <span class="badge bg-secondary">{{ getProductById(productId).specs.interface || getProductById(productId).specs.type }}</span>
                    </div>
                  </div>
                  <button class="btn btn-sm btn-outline-danger" @click="removeProduct(productId)">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Comparison Results -->
    <div v-if="comparisonResults && selectedProducts.length >= 2" class="row mb-4">
      <div class="col-12">
        <div class="card border-0 shadow-sm mb-3" style="background: linear-gradient(135deg, #28a745, #20c997);">
          <div class="card-body text-white">
            <h5 class="mb-0">
                <i class="fas fa-chart-bar me-2"></i>{{ $t('compare.comparisonResults') }}
              </h5>
          </div>
        </div>
        
        <div class="card border-0 shadow-sm">
          <div class="card-body">
            <!-- Comparison Table -->
            <div class="table-responsive">
              <table class="table table-hover">
                <thead class="table-light">
                  <tr>
                    <th>{{ $t('compare.product') }}</th>
                    <th>{{ $t('compare.price') }}</th>
                    <th>{{ $t('compare.rating') }}</th>
                    <th>{{ $t('compare.capacitySpeed') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="product in getSelectedProductsData()" :key="product.id">
                    <td>
                      <div>
                        <strong>{{ product.brand }}</strong>
                        <br>
                        <small class="text-muted">{{ product.name.substring(0, 50) }}...</small>
                      </div>
                    </td>
                    <td>
                      <span class="fw-bold" :class="getPriceClass(product.price)">
                        ${{ product.price }}
                      </span>
                    </td>
                    <td>
                      <div class="d-flex align-items-center">
                        <span class="me-2">{{ product.rating }}/5</span>
                        <div class="progress" style="width: 60px; height: 8px;">
                          <div class="progress-bar bg-warning" :style="{ width: (product.rating / 5 * 100) + '%' }"></div>
                        </div>
                      </div>
                    </td>
                    <td>{{ product.specs.capacity || product.specs.speed }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            

          </div>
        </div>
      </div>
    </div>

    <!-- Category Analysis -->
    <div class="row mb-4">
      <!-- Loading State -->
      <div v-if="analysisLoading" class="col-12 text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">{{ $t('compare.loadingAnalysis') }}</span>
        </div>
        <p class="mt-2 text-muted">{{ $t('compare.loadingMarketAnalysis') }}</p>
      </div>
      
      <!-- Error State -->
      <div v-else-if="analysisError" class="col-12">
        <div class="alert alert-warning">
          <i class="fas fa-exclamation-triangle me-2"></i>
          {{ $t('compare.failedToLoadAnalysisData') }}. {{ $t('compare.showingDefaultAnalysisData') }}
        </div>
      </div>
      
      <!-- Analysis Data -->
      <div v-for="category in categoryAnalysisData" :key="category.category" class="col-lg-6 mb-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-header" style="background: linear-gradient(135deg, #6f42c1, #8b5cf6);">
            <h5 class="text-white mb-0">
              <i :class="getCategoryIcon(category.category)" class="me-2"></i>
              {{ category.category_display }} Analysis
            </h5>
            <small class="text-white-50">Updated: {{ formatDate(category.analysis_date) }}</small>
          </div>
          <div class="card-body">
            <!-- Market Leaders -->
            <div class="mb-3">
              <h6>Market Leaders</h6>
              <div v-for="leader in category.market_leaders" :key="leader.brand" class="d-flex justify-content-between mb-2">
                <span>{{ leader.brand }}</span>
                <div class="d-flex align-items-center gap-2">
                  <span class="badge" :class="getMarketShareBadgeClass(leader.market_share)">
                    {{ leader.market_share }}%
                  </span>
                  <small class="text-muted">({{ leader.product_count }} products)</small>
                </div>
              </div>
            </div>
            
            <!-- Price Ranges -->
            <div class="mb-3">
              <h6>Price Ranges</h6>
              <div v-for="range in category.price_ranges" :key="range.tier" class="d-flex justify-content-between">
                <small>{{ range.tier }}</small>
                <div class="d-flex align-items-center gap-2">
                  <small :class="getPriceRangeClass(range.min_price)">
                    ${{ range.min_price }} - ${{ range.max_price }}
                  </small>
                  <small class="text-muted">({{ range.product_count }})</small>
                </div>
              </div>
            </div>
            
            <!-- Popular Items -->
            <div>
              <h6>Popular {{ category.category === 'DRAM' ? 'Specs' : 'Features' }}</h6>
              <div class="d-flex flex-wrap gap-1">
                <span 
                  v-for="item in category.popular_items" 
                  :key="item.item" 
                  class="badge bg-light text-dark"
                  :title="`${item.frequency}% frequency`"
                >
                  {{ item.item }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Fallback: Show default data if no API data -->
      <div v-if="!analysisLoading && !categoryAnalysisData.length" class="col-lg-6 mb-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-header" style="background: linear-gradient(135deg, #6f42c1, #8b5cf6);">
            <h5 class="text-white mb-0">
              <i class="fas fa-memory me-2"></i>DDR Memory Analysis
            </h5>
          </div>
          <div class="card-body">
            <div class="mb-3">
              <h6>Market Leaders</h6>
              <div class="d-flex justify-content-between mb-2">
                <span>Corsair</span>
                <span class="badge bg-primary">32.4%</span>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span>Kingston</span>
                <span class="badge bg-info">28.7%</span>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span>ADATA</span>
                <span class="badge bg-success">18.2%</span>
              </div>
            </div>
            
            <div class="mb-3">
              <h6>Price Range</h6>
              <div class="d-flex justify-content-between">
                <small>Entry Level</small>
                <small class="text-success">$45 - $89</small>
              </div>
              <div class="d-flex justify-content-between">
                <small>Mid Range</small>
                <small class="text-warning">$90 - $179</small>
              </div>
              <div class="d-flex justify-content-between">
                <small>High End</small>
                <small class="text-danger">$180 - $350</small>
              </div>
            </div>
            
            <div>
              <h6>Popular Specs</h6>
              <div class="d-flex flex-wrap gap-1">
                <span class="badge bg-light text-dark">DDR4-3200</span>
                <span class="badge bg-light text-dark">16GB</span>
                <span class="badge bg-light text-dark">RGB</span>
                <span class="badge bg-light text-dark">Low Profile</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div v-if="!analysisLoading && !categoryAnalysisData.length" class="col-lg-6 mb-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-header" style="background: linear-gradient(135deg, #6f42c1, #8b5cf6);">
            <h5 class="text-white mb-0">
              <i class="fas fa-hdd me-2"></i>SSD Storage Analysis
            </h5>
          </div>
          <div class="card-body">
            <div class="mb-3">
              <h6>Market Leaders</h6>
              <div class="d-flex justify-content-between mb-2">
                <span>Samsung</span>
                <span class="badge bg-primary">35.6%</span>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span>ADATA</span>
                <span class="badge bg-success">24.7%</span>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span>Crucial</span>
                <span class="badge bg-info">19.3%</span>
              </div>
            </div>
            
            <div class="mb-3">
              <h6>Price Range</h6>
              <div class="d-flex justify-content-between">
                <small>250GB - 500GB</small>
                <small class="text-success">$35 - $89</small>
              </div>
              <div class="d-flex justify-content-between">
                <small>1TB</small>
                <small class="text-warning">$90 - $149</small>
              </div>
              <div class="d-flex justify-content-between">
                <small>2TB+</small>
                <small class="text-danger">$150 - $399</small>
              </div>
            </div>
            
            <div>
              <h6>Popular Features</h6>
              <div class="d-flex flex-wrap gap-1">
                <span class="badge bg-light text-dark">NVMe</span>
                <span class="badge bg-light text-dark">PCIe 4.0</span>
                <span class="badge bg-light text-dark">DRAM Cache</span>
                <span class="badge bg-light text-dark">5 Year Warranty</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import apiClient from '../api';
import { getTokens, setTokens } from '../utils/auth';

const loading = ref(false);
const error = ref(null);
const selectedCategory = ref('');
const selectedBrand = ref('');
const selectedProducts = ref([]);
const comparisonResults = ref(null);

// Products data from API
const allProducts = ref([]);
const categoryAnalysisData = ref([]);
const analysisLoading = ref(false);
const analysisError = ref(null);
const totalProductsAnalyzed = ref(0);

// Computed properties
const availableCategories = computed(() => {
  const categories = [...new Set(allProducts.value.map(p => p.category))];
  return categories.sort();
});

const availableBrands = computed(() => {
  const brands = [...new Set(allProducts.value.map(p => p.brand))];
  return brands.sort();
});

const filteredProducts = computed(() => {
  let filtered = allProducts.value;
  
  if (selectedCategory.value) {
    filtered = filtered.filter(p => p.category === selectedCategory.value);
  }
  
  if (selectedBrand.value) {
    filtered = filtered.filter(p => p.brand === selectedBrand.value);
  }
  
  return filtered;
});

const selectedProductsData = computed(() => {
  return selectedProducts.value.map(id => getProductById(id)).filter(p => p.id);
});



// Methods
const getProductById = (id) => {
  const product = allProducts.value.find(p => p.id === id);
  return product || {
    id: null,
    name: 'Unknown Product',
    brand: 'Unknown',
    category: 'Unknown',
    price: 0,
    rating: 0,
    marketShare: 0,
    valueScore: 0,
    specs: {
      capacity: 'N/A',
      speed: 'N/A',
      interface: 'N/A',
      type: 'N/A'
    }
  };
};

const getSelectedProductsData = () => {
  return selectedProductsData.value;
};

const filterProducts = () => {
  // This method is called when filters change
  // The computed property will automatically update
};

const compareProducts = async () => {
  if (selectedProducts.value.length >= 2) {
    try {
      loading.value = true;
      
      // Call the real comparison API
      const response = await apiClient.post('/products/compare', {
        product_ids: selectedProducts.value
      });
      
      if (response.data) {
        comparisonResults.value = {
          products: getSelectedProductsData(),
          timestamp: new Date().toISOString(),
          apiData: response.data
        };
      } else {
        // Fallback to local comparison
        comparisonResults.value = {
          products: getSelectedProductsData(),
          timestamp: new Date().toISOString()
        };
      }
      
      loading.value = false;
    } catch (err) {
      console.warn('API comparison failed, using local data:', err);
      // Fallback to local comparison
      comparisonResults.value = {
        products: getSelectedProductsData(),
        timestamp: new Date().toISOString()
      };
      loading.value = false;
    }
  }
};

const clearSelection = () => {
  selectedProducts.value = [];
  comparisonResults.value = null;
};

const removeProduct = (productId) => {
  const index = selectedProducts.value.indexOf(productId);
  if (index > -1) {
    selectedProducts.value.splice(index, 1);
    if (selectedProducts.value.length < 2) {
      comparisonResults.value = null;
    } else {
      compareProducts();
    }
  }
};

const getPriceClass = (price) => {
  if (price < 50) return 'text-success';
  if (price < 150) return 'text-warning';
  return 'text-danger';
};



// Analysis helper methods
const getCategoryIcon = (category) => {
  const iconMap = {
    'DRAM': 'fas fa-memory',
    'SSD': 'fas fa-hdd',
    'USB_DRIVE': 'fas fa-usb'
  };
  return iconMap[category] || 'fas fa-microchip';
};

const getMarketShareBadgeClass = (share) => {
  if (share >= 30) return 'bg-primary';
  if (share >= 20) return 'bg-info';
  if (share >= 15) return 'bg-success';
  return 'bg-secondary';
};

const getPriceRangeClass = (minPrice) => {
  if (minPrice < 50) return 'text-success';
  if (minPrice < 150) return 'text-warning';
  return 'text-danger';
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString();
  } catch {
    return dateString;
  }
};



const fetchProducts = async () => {
  try {
    loading.value = true;
    error.value = null;
    
    // Ensure we have valid authentication
    const isAuthenticated = await ensureAuthentication();
    if (!isAuthenticated) {
      throw new Error('Authentication failed');
    }
    
    // Fetch products from API with a reasonable page size
    const response = await apiClient.get('/products', {
      params: {
        page: 1,
        page_size: 10000, // Get more products for comparison
        sort_by: 'name',
        order: 'asc'
      }
    });
    
    if (response.data && response.data.data) {
      // Transform API data to match component expectations
      allProducts.value = response.data.data.map(product => ({
        id: product.id,
        name: product.product_name,
        brand: product.brand,
        category: mapCategoryToDisplay(product.category),
        price: product.price || 0,
        rating: generateRating(), // Since API doesn't have rating, generate one
        specs: generateSpecs(product.category, product.product_name)
      }));
    }
    
    loading.value = false;
  } catch (err) {
    error.value = 'Failed to load products data. Please check your connection.';
    console.error('Failed to fetch products:', err);
    loading.value = false;
  }
};

// Helper functions to transform API data
const mapCategoryToDisplay = (category) => {
  const categoryMap = {
    'DRAM': 'DDR Memory',
    'SSD': 'SSD Storage',
    'USB': 'USB Drives'
  };
  return categoryMap[category] || category;
};

const generateRating = () => {
  // Generate a realistic rating between 4.0 and 5.0
  return Math.round((4.0 + Math.random() * 1.0) * 10) / 10;
};



const generateSpecs = (category, productName) => {
  // Generate realistic specs based on category and product name
  const name = (productName || '').toLowerCase();
  
  if (category === 'DDR Memory') {
    // Extract capacity from product name
    let capacity = '16GB';
    if (name.includes('8gb')) capacity = '8GB';
    else if (name.includes('16gb')) capacity = '16GB';
    else if (name.includes('32gb')) capacity = '32GB';
    else if (name.includes('64gb')) capacity = '64GB';
    
    // Extract speed from product name
    let speed = 'DDR4-3200';
    if (name.includes('ddr5')) {
      if (name.includes('4800')) speed = 'DDR5-4800';
      else if (name.includes('5600')) speed = 'DDR5-5600';
      else if (name.includes('6000')) speed = 'DDR5-6000';
      else speed = 'DDR5-4800';
    } else {
      if (name.includes('2666')) speed = 'DDR4-2666';
      else if (name.includes('3200')) speed = 'DDR4-3200';
      else if (name.includes('3600')) speed = 'DDR4-3600';
      else speed = 'DDR4-3200';
    }
    
    return {
      capacity,
      speed,
      'interface': 'DIMM',
      type: name.includes('rgb') ? 'RGB Gaming' : 'Standard'
    };
  } else if (category === 'SSD Storage') {
    // Extract capacity from product name
    let capacity = '1TB';
    if (name.includes('256gb') || name.includes('256')) capacity = '256GB';
    else if (name.includes('512gb') || name.includes('512')) capacity = '512GB';
    else if (name.includes('1tb') || name.includes('1000')) capacity = '1TB';
    else if (name.includes('2tb') || name.includes('2000')) capacity = '2TB';
    
    // Extract interface from product name
    let productInterface = 'SATA III';
    if (name.includes('nvme') || name.includes('pcie')) {
      productInterface = name.includes('4.0') ? 'NVMe PCIe 4.0' : 'NVMe PCIe 3.0';
    } else if (name.includes('sata')) {
      productInterface = 'SATA III';
    }
    
    return {
      capacity,
      interface: productInterface,
      type: name.includes('m.2') ? 'M.2 2280' : '2.5" Internal',
      speed: productInterface.includes('4.0') ? '7000/6900 MB/s' : '3500/3000 MB/s'
    };
  } else {
    // USB Drives
    let capacity = '128GB';
    if (name.includes('32gb')) capacity = '32GB';
    else if (name.includes('64gb')) capacity = '64GB';
    else if (name.includes('128gb')) capacity = '128GB';
    else if (name.includes('256gb')) capacity = '256GB';
    else if (name.includes('1tb')) capacity = '1TB';
    
    return {
      capacity,
      interface: name.includes('3.1') ? 'USB 3.1' : 'USB 3.2',
      type: 'Flash Drive',
      speed: capacity === '1TB' ? '1000/900 MB/s' : '100 MB/s'
    };
  }
};

const ensureAuthentication = async () => {
  const { accessToken } = getTokens();
  if (!accessToken) {
    console.log('⚠️ No access token found, attempting auto-login...');
    try {
      // Auto-login with default admin credentials for testing
      const params = new URLSearchParams();
      params.append('username', 'admin');
      params.append('password', 'admin123');
      
      const loginResponse = await apiClient.post('/auth/login', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      
      if (loginResponse.data?.data?.access_token) {
        const { access_token, refresh_token } = loginResponse.data.data;
        setTokens(access_token, refresh_token);
        console.log('✅ Auto-login successful');
        return true;
      }
    } catch (loginError) {
      console.error('❌ Auto-login failed:', loginError);
      return false;
    }
  }
  return true;
};

const fetchProductAnalysis = async () => {
  try {
    analysisLoading.value = true;
    analysisError.value = null;
    
    console.log('📊 Fetching product analysis data...');
    
    // Ensure we have valid authentication
    const isAuthenticated = await ensureAuthentication();
    if (!isAuthenticated) {
      throw new Error('Authentication failed');
    }
    
    const response = await apiClient.get('/products/analysis');
    
    if (response.data) {
      categoryAnalysisData.value = response.data.data || [];
      totalProductsAnalyzed.value = response.data.total_products_analyzed || 0;
      console.log('✅ Analysis data loaded:', categoryAnalysisData.value.length, 'categories, Total Products Analyzed:', totalProductsAnalyzed.value);
    }
    
    analysisLoading.value = false;
  } catch (err) {
    analysisError.value = 'Failed to load analysis data';
    console.error('❌ Analysis API Failed:', err);
    console.error('Error details:', {
      status: err.response?.status,
      statusText: err.response?.statusText,
      data: err.response?.data,
      url: err.config?.url
    });
    analysisLoading.value = false;
  }
};



const fetchInitialData = async () => {
  await Promise.all([
    fetchProducts(),
    fetchProductAnalysis()
  ]);
};

onMounted(fetchInitialData);
</script>

<style scoped>
.card {
  transition: transform 0.2s ease-in-out;
}

.card:hover {
  transform: translateY(-2px);
}

.btn.active {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.opacity-50 {
  opacity: 0.5;
}

.form-check-input:checked {
  background-color: #007bff;
  border-color: #007bff;
}

.fixed-checkbox-width {
  width: 20px; /* Adjust as needed */
  min-width: 20px; /* Ensure it doesn't shrink */
  height: 20px; /* Make it square */
}

.fixed-rating-width {
  width: 50px; /* Adjust as needed to fit 'X.X/5' */
  text-align: center;
}
</style>
